import axios from 'axios';
import moment from 'moment';
import { aadConfig, getAccessToken } from '../aad';
import type {
  Account,
  AccountRelationship,
  AccountWithUsers,
  AllProvidersTransactionAggregateDto,
  Client,
  IntegrationRequestDto,
  LaborRate,
  OdcItem,
  Office,
  RelationshipStatus,
  ResourceUser,
  ResourceUserRole,
  User,
} from '../dto/account';
import type { PagedItems, Patch } from '../dto/common';
import type { CreateInvitation, Invitation } from '../dto/invitation';
import type { Project } from '../dto/project';
import type { Transaction } from '../dto/transaction';
import apiProxy from './apiProxy';

interface CreateSubscriptionRequest {
  cardToken: string;
  amount: number;
}

interface UpdateSubscriptionRequest {
  cardToken: string;
  amount: number;
}

/** Create an account. */
export const createAccount = async (
  account?: Account,
  user?: User,
  logo?: File,
  picture?: File,
): Promise<{ account?: Account; user?: User }> => {
  await getAccessToken(aadConfig().accountScopes);

  let a: Account | undefined;
  if (account) {
    const { data } = await axios.post<Account>(`${process.env.ACCOUNT_API}/accounts`, account);
    a = data;

    if (logo instanceof File) await uploadAccountLogo(a.id, logo);
  }

  if (user) {
    const { data: u } = await axios.put<User>(`${process.env.ACCOUNT_API}/users/me?onboard=complete`, user);
    user = u;

    if (picture instanceof File) await uploadUserPicture(user.id, picture);
  }

  return { account: a, user };
};

export const updateAccount = async (account: Account, logo?: File, banner?: File): Promise<Account> => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.put<Account>(`${process.env.ACCOUNT_API}/accounts/${account.id}`, account);

  try {
    if (logo) await uploadAccountLogo(account.id, logo);
  } catch {
    // DO NOTHING
  }
  try {
    if (banner) await uploadAccountBanner(account.id, banner);
  } catch {
    // DO NOTHING
  }

  return data;
};

export const linkAccountToProvider = async (clientAccountId: string, providerAccountId: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  const links = [{ op: 'Add', clientAccountId, providerAccountId }];
  await axios.post(`${process.env.ACCOUNT_API}/accounts/link`, links);
};

export const unlinkProviderFromClient = async (clientAccountId: string, providerAccountId: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  const links = [{ op: 'remove', clientAccountId, providerAccountId }];
  await axios.post(`${process.env.ACCOUNT_API}/accounts/link`, links);
};
export const getProviderByEmail = async (email: string): Promise<Account | null> => {
  try {
    const { data } = await axios.get<Account>(`${process.env.ACCOUNT_API}/accounts/provider-by-email`, {
      params: { email },
    });
    return data ?? null;
  } catch (err) {
    // biome-ignore lint/suspicious/noExplicitAny: error narrowing
    if ((err as any)?.response?.status === 404) return null;
    throw err;
  }
};

export const setAccountPersona = async (accountId: string, persona: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  const patch = [{ propertyName: 'Persona', propertyValue: persona }];
  await axios.patch(`${process.env.ACCOUNT_API}/accounts/${accountId}`, patch);
};

/** Mark a linked provider active/inactive for a client (inactive hides it from project creation). */
export const setProviderActive = async (accountId: string, providerId: string, active: boolean): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  await axios.put(`${process.env.ACCOUNT_API}/accounts/${accountId}/providers/${providerId}/active?active=${active}`);
};
/** Mark a linked client active/inactive for a provider (inactive hides it from project creation). */
export const setClientActive = async (accountId: string, clientId: string, active: boolean): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  await axios.put(`${process.env.ACCOUNT_API}/accounts/${accountId}/clients/${clientId}/active?active=${active}`);
};
export const deleteAccount = async (id: string, force?: boolean): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);

  await axios.delete(`${process.env.ACCOUNT_API}/accounts/${id}`, {
    params: force ? { force: true } : undefined,
  });
};

/** Upload account logo. */
export const uploadAccountLogo = async (accountId: string, logo: File): Promise<Account> => {
  await getAccessToken(aadConfig().accountScopes);

  const body = new FormData();
  body.append('logo', logo);

  const { data } = await axios.post<Account>(`${process.env.ACCOUNT_API}/accounts/${accountId}/logo`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
};

export const uploadAccountBanner = async (accountId: string, banner: File): Promise<Account> => {
  await getAccessToken(aadConfig().accountScopes);

  const body = new FormData();
  body.append('banner', banner);

  const { data } = await axios.post<Account>(`${process.env.ACCOUNT_API}/accounts/${accountId}/banner`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
};

export const createDefaultAccountRates = async (accountId: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  // POST request without a body to trigger default rate creation
  await axios.post(`${process.env.ACCOUNT_API}/rates/${accountId}`);
  // No return value needed as backend returns Ok()
};

export const deleteAccountRate = async (accountId: string, rateId: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  await axios.delete(`${process.env.ACCOUNT_API}/rates/${accountId}/${rateId}`);
};

/** Update a user */
export const updateUser = async (
  accountId: string | null | undefined,
  id: string | null | undefined,
  user: User,
  picture?: File,
  onboard?: string,
): Promise<User> => {
  await getAccessToken(aadConfig().accountScopes);

  const u = { ...user, accountId };

  const { data } = await axios.put<User>(
    `${process.env.ACCOUNT_API}/users/${id || 'me'}${onboard ? `?onboard=${onboard}` : ''}`,
    u,
  );

  try {
    if (picture) await uploadUserPicture(u.id, picture);
  } catch {
    // DO NOTHING
  }

  return data;
};

/** Upload account logo. */
export const uploadUserPicture = async (userId: string, picture: File): Promise<User> => {
  await getAccessToken(aadConfig().accountScopes);

  const body = new FormData();
  body.append('picture', picture);

  const { data } = await axios.post<User>(`${process.env.ACCOUNT_API}/users/${userId}/picture`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
};

/** Fetch an account. */
export const getAccountById = apiProxy('getAccountById', async (id: string): Promise<Account> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${id}`);
  return data;
});

/** Fetch all user accounts. */
export const getAccounts = apiProxy('getAccounts', async (ids?: string[]): Promise<Account[]> => {
  await getAccessToken(aadConfig().accountScopes);
  const abort = new AbortController();
  try {
    const { data } = await axios.get<Account[]>(
      `${process.env.ACCOUNT_API}/accounts${ids ? `?ids=${ids.join(',')}` : ''}`,
      {
        signal: abort.signal,
      },
    );
    return data;
  } catch (err) {
    if (!abort.signal.aborted) console.error(err);
    return [];
  }
});

/**
 * One account's relationships, from the edge model.
 *
 * Optionally narrowed to one side. The account is the partition these live in, so this is a
 * single-partition read and the server refuses it for anyone not on that account.
 */
export const getAccountRelationships = apiProxy(
  'getAccountRelationships',
  async (accountId: string, party?: 'provider' | 'client'): Promise<AccountRelationship[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const q = party ? `?party=${party}` : '';
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/relationships${q}`);
    return data ?? [];
  },
);

/** Record that two accounts work together. Writes both sides. */
export const upsertAccountRelationship = async (
  accountId: string,
  counterpartyAccountId: string,
  party: 'provider' | 'client',
  status: RelationshipStatus = 'active',
  /** What the firm does for this account. Only ever added to, never cleared. */
  category?: string,
  /** What the customer calls it, when the category is 'other'. */
  categoryLabel?: string,
): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  const q = [`party=${party}`, `status=${status}`];
  if (category) q.push(`category=${encodeURIComponent(category)}`);
  if (categoryLabel) q.push(`categoryLabel=${encodeURIComponent(categoryLabel)}`);
  await axios.put(
    `${process.env.ACCOUNT_API}/accounts/${accountId}/relationships/${counterpartyAccountId}?${q.join('&')}`,
  );
};

/** Active or inactive. Inactive stays in the network and leaves the project pickers. */
export const setAccountRelationshipStatus = async (
  accountId: string,
  counterpartyAccountId: string,
  status: RelationshipStatus,
): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  await axios.put(
    `${process.env.ACCOUNT_API}/accounts/${accountId}/relationships/${counterpartyAccountId}/status?status=${status}`,
  );
};

/**
 * Who owns an account, and how that changes.
 *
 * Ownership carries the firm's settings, its team and its plan, so it never moves on one person's
 * say-so. Assuming an unclaimed account is immediate because the person doing it is the one who
 * wants it; everything else is an offer the recipient has to accept, and a handover needs the
 * current owner's confirmation as well. The server enforces all of that - these are just the
 * calls.
 */
export type OwnershipTransfer = {
  id: string;
  accountId: string;
  kind: 'Claim' | 'Handover';
  fromUserId?: string | null;
  toUserId: string;
  toEmailAddress?: string | null;
  recipientAcceptedAt?: string | null;
  ownerConfirmedAt?: string | null;
  status: 'Pending' | 'Completed' | 'Declined' | 'Cancelled';
  awaiting: string;
};

/**
 * Take ownership of an account that has none. Immediate: there is no second party.
 *
 * Returns the claimed account. A claim changes the account in place - its status moves to Ready
 * and the caller becomes its owner - without changing its id, so anything holding the old copy
 * cannot tell by looking that it is stale. This used to resolve to void, which is how a claim
 * that had plainly succeeded came to look like nothing happening at all.
 */
export const assumeAccountOwnership = apiProxy(
  'assumeAccountOwnership',
  async (accountId: string): Promise<Account> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(`${process.env.ACCOUNT_API}/accounts/${accountId}/ownership/assume`);
    return data;
  },
);

/** The account's open ownership transfers. */
export const getOwnershipTransfers = apiProxy(
  'getOwnershipTransfers',
  async (accountId: string): Promise<OwnershipTransfer[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/ownership/transfers`);
    return data;
  },
);

/** Offer ownership to someone. Nothing moves until they accept. */
export const offerAccountOwnership = apiProxy(
  'offerAccountOwnership',
  async (accountId: string, toUserId: string, toEmailAddress?: string): Promise<OwnershipTransfer> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(`${process.env.ACCOUNT_API}/accounts/${accountId}/ownership/transfers`, {
      toUserId,
      toEmailAddress,
    });
    return data;
  },
);

/** The recipient's signature. */
export const acceptAccountOwnership = apiProxy(
  'acceptAccountOwnership',
  async (accountId: string, transferId: string): Promise<OwnershipTransfer> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/ownership/transfers/${transferId}/accept`,
    );
    return data;
  },
);

/** The current owner's signature on a handover. */
export const confirmAccountOwnership = apiProxy(
  'confirmAccountOwnership',
  async (accountId: string, transferId: string): Promise<OwnershipTransfer> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/ownership/transfers/${transferId}/confirm`,
    );
    return data;
  },
);

/** End a pending transfer. Either party may. */
export const declineAccountOwnership = apiProxy(
  'declineAccountOwnership',
  async (accountId: string, transferId: string): Promise<OwnershipTransfer> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/ownership/transfers/${transferId}/decline`,
    );
    return data;
  },
);

/** Take a relationship out of the network, both sides. */
export const removeAccountRelationship = async (accountId: string, counterpartyAccountId: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  await axios.delete(`${process.env.ACCOUNT_API}/accounts/${accountId}/relationships/${counterpartyAccountId}`);
};

/** Fetch all client accounts. */
export const getClientAccounts = apiProxy(
  'getClientAccounts',
  async (accountId: string, includeInactive = false): Promise<Account[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const q = includeInactive ? '?includeInactive=true' : '';
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/client-accounts${q}`);
    return data;
  },
);

/** Fetch all provider accounts. */
export const getProviderAccounts = apiProxy(
  'getProviderAccounts',
  async (accountId: string, includeInactive = false): Promise<Account[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const q = includeInactive ? '?includeInactive=true' : '';
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/provider-accounts${q}`);
    return data;
  },
);

/** Fetch all client account by the provider id. */
export const getAccountsByAccount = apiProxy(
  'getAccountsByAccount',
  async (
    accountId: string,
    offset: number,
    limit: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
    noCounts?: boolean,
  ): Promise<{ total: number; accounts: Account[] }> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset}`, `limit=${limit}`];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);
    if (noCounts === true) q.push('noCounts=true');

    const { data } = await axios.get(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/list${q.length ? `?${q.join('&')}` : ''}`,
    );
    return data;
  },
);

/** Get account users. */
export const getAccountUsers = apiProxy(
  'getAccountUsers',
  async (accountId: string, projectId?: string): Promise<AccountWithUsers> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [];
    if (projectId) q.push(`projectId=${projectId}`);

    const { data } = await axios.get(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/users${q.length ? `?${q.join('&')}` : ''}`,
    );
    return data;
  },
);

/** Get account counts. */
export const getAccountCounts = apiProxy(
  'getAccountCounts',
  async (accountId: string): Promise<{ [key: string]: number }> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/counts`);
    return data;
  },
);

/** Link account users. */
export const linkAccountUser = apiProxy(
  'linkAccountUser',
  async (accountId: string, users: ResourceUser[]): Promise<void> => {
    await getAccessToken(aadConfig().accountScopes);

    const patch: Patch[] = [
      {
        propertyName: 'Users',
        propertyValue: users,
      },
    ];

    await axios.patch(`${process.env.ACCOUNT_API}/accounts/${accountId}`, patch);
  },
);

/** Delete account users. */
export const deleteAccountUser = apiProxy(
  'deleteAccountUser',
  async (accountId: string, userId: string): Promise<void> => {
    await getAccessToken(aadConfig().accountScopes);
    await axios.delete(`${process.env.ACCOUNT_API}/accounts/${accountId}/users/${userId}`);
  },
);

/** Delete account users. */
export const updateAccountUserRole = apiProxy(
  'updateAccountUserRole',
  async (accountId: string, userId: string, role: ResourceUserRole): Promise<void> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`role=${role}`];
    await axios.put(`${process.env.ACCOUNT_API}/accounts/${accountId}/users/${userId}?${q.join('&')}`);
  },
);

/** Fetch account offices. */
export const getOffices = apiProxy(
  'getOffices',
  async (
    accountId: string,
    offset: number,
    limit: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
  ): Promise<{ offices: Office[]; total: number }> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset}`, `limit=${limit}`];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    const { data } = await axios.get(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/offices${q.length ? `?${q.join('&')}` : ''}`,
    );
    return data;
  },
);

/** Fetch a single office. */
export const getOffice = apiProxy('getOffice', async (_accountId: string, officeId: string): Promise<Office> => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.get(`${process.env.ACCOUNT_API}/offices/${officeId}`);
  return data;
});

/** Create/update account office. */
export const upsertOffice = apiProxy('upsertOffice', async (accountId: string, office: Office): Promise<Office> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.post(`${process.env.ACCOUNT_API}/accounts/${accountId}/offices`, office);
  return data;
});

/** Delete account office. */
export const deleteOffice = apiProxy('deleteOffice', async (accountId: string, officeId: string): Promise<void> => {
  await getAccessToken(aadConfig().accountScopes);
  await axios.delete(`${process.env.ACCOUNT_API}/accounts/${accountId}/offices/${officeId}`);
});

/** Delete account office user. */
export const deleteOfficeUser = apiProxy(
  'deleteOfficeUser',
  async (accountId: string, officeId: string, userId: string): Promise<void> => {
    await getAccessToken(aadConfig().accountScopes);
    await axios.delete(`${process.env.ACCOUNT_API}/accounts/${accountId}/offices/${officeId}/users/${userId}`);
  },
);

/** Delete account office. */
export const updateOfficeUserRole = apiProxy(
  'updateOfficeUserRole',
  async (accountId: string, officeId: string, userId: string, role: ResourceUserRole): Promise<void> => {
    await getAccessToken(aadConfig().accountScopes);
    await axios.put(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/offices/${officeId}/users/${userId}?role=${role}`,
    );
  },
);

/** Fetch account clients. */
export const getClients = apiProxy('getClients', async (accountId: string): Promise<Client[]> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/clients`);
  return data;
});

export type GetMeArgs = {
  providerId?: string;
  action?: string | null;
  type?: string | null;
  abort?: AbortController;
};
export const getMe = apiProxy('getMe', async (args?: GetMeArgs): Promise<User | undefined> => {
  await getAccessToken(aadConfig().accountScopes);

  const { providerId, action, type, abort } = args ?? {};

  const params: string[] = [];
  if (providerId) params.push(`providerId=${providerId}`);
  if (action) params.push(`action=${action}`);
  if (type) params.push(`type=${type}`);

  try {
    const { data } = await axios.get<User>(
      `${process.env.ACCOUNT_API}/users/me${params.length ? `?${params.join('&')}` : ''}`,
      { signal: abort?.signal },
    );

    data.providerId = providerId;
    return data;
  } catch (err) {
    /*
     * An aborted request is not a failure - the caller walked away - so it resolves undefined as
     * before. Anything else is rethrown.
     *
     * This used to swallow every error and resolve undefined, which turned a failing /users/me
     * into a promise that looked like it had succeeded with no user. The caller could not tell a
     * signed-out user from a server that was refusing, so it sat in its loading state forever
     * rather than showing anybody what had gone wrong.
     */
    if (abort?.signal.aborted) return undefined;

    console.error(err);
    throw err;
  }
});

export interface GetUsersParams {
  [key: string]: string | string[] | number | boolean | undefined | null;
  ids?: string[];
  lat?: number;
  lon?: number;
  accountId?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  disciplines?: string[];
  category?: number;
  projectTypeId?: string;
  search?: string;
  country?: string;
  countrySubdivision?: string;
  municipality?: string;
  providerOnly?: boolean;
  status?: string | undefined;
  expertType?: string;
  offset?: number;
  limit?: number;
}
export const getUsers = apiProxy('getUsers', async (params: GetUsersParams): Promise<User[]> => {
  await getAccessToken(aadConfig().accountScopes);

  const qs: string[] = [];
  Object.keys(params).forEach((k) => {
    if (params[k] == null || params[k] === undefined) return;

    if (k === 'ids') {
      qs.push(`${k}=${encodeURIComponent(params[k]!.join(','))}`);
      return;
    }

    if (k === 'lat' || k === 'lon' || k === 'offset' || k === 'limit' || k === 'category') {
      qs.push(`${k}=${encodeURIComponent(params[k]!.toString())}`);
      return;
    }
    if (k === 'disciplines') {
      qs.push(`${k}=${encodeURIComponent((params[k] as string[]).join(','))}`);
      return;
    }
    qs.push(`${k}=${encodeURIComponent(params[k] as string)}`);
  });

  const { data } = await axios.get(`${process.env.ACCOUNT_API}/users?${qs.join('&')}`);
  return data;
});

/** Get expert users with pagination. */
export const getExpertUsers = apiProxy(
  'getExpertUsers',
  async (offset: number, limit: number, expertType?: string): Promise<User[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const params = new URLSearchParams();
    params.append('offset', offset.toString());
    params.append('limit', limit.toString());
    if (expertType) {
      params.append('expertType', expertType);
    }
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/users/experts?${params.toString()}`);
    return data;
  },
);

/** Get expert user by ID. */
export const getExpertUserById = apiProxy('getExpertUserById', async (id: string): Promise<User> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.get(`${process.env.ACCOUNT_API}/users/${id}`);
  return data;
});

/** Fetch provider users. */
export const getProviderUsers = apiProxy('getProviderUsers', async (id: string): Promise<User[]> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.get(`${process.env.ACCOUNT_API}/users/${id}`);
  return data;
});

/** Fetch all company types. */
export const getCompanyTypes = apiProxy('getCompanyTypes', async (): Promise<{ [key: string]: string }> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.get(`${process.env.ACCOUNT_API}/support/companytypes`);
  return data;
});

/** Create an invitation. */
export const createInvitation = apiProxy(
  'createInvitation',
  async (accountId: string, create: CreateInvitation): Promise<Invitation> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(`${process.env.ACCOUNT_API}/accounts/${accountId}/invitations`, create);
    return data;
  },
);

/** Verify the invitation. */
export const verifyInvitation = apiProxy('verifyInvitation', async (key: string): Promise<Invitation> => {
  const { data } = await axios.get(`${process.env.ACCOUNT_API}/invitations?key=${encodeURIComponent(key)}`);
  return data;
});

/** Complete the invitation. */
export const completeInvitation = apiProxy(
  'completeInvitation',
  async (
    key: string,
    /**
     * The name of the person accepting, where they have had to give it.
     *
     * The server refuses an acceptance it cannot resolve a complete name for, from this, from
     * their existing record, or from the invitation. A firm invite carries none on purpose, so for
     * those this is the only place the name can come from.
     */
    name?: { givenName: string; familyName: string },
  ): Promise<{
    account: Account;
    project?: Project;
    onboardClient?: boolean;
    /** The company that sent the invitation, server-derived from the invitation itself. */
    invitedBy?: { companyName?: string; logoUrl?: string };
  }> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${process.env.ACCOUNT_API}/invitations?key=${encodeURIComponent(key)}`,
      name ?? {},
    );
    return data;
  },
);

/** Delete an invitation. */
export const deleteInvitation = apiProxy(
  'deleteInvitation',
  async (accountId: string, invitationId: string): Promise<Invitation> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.delete(`${process.env.ACCOUNT_API}/accounts/${accountId}/invitations/${invitationId}`);
    return data;
  },
);

/** Resend an invitation. */
export const resendInvitation = apiProxy(
  'resendInvitation',
  async (accountId: string, invitationId: string): Promise<Invitation> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.put(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/invitations/${invitationId}/resend`,
    );
    return data;
  },
);

export const schoolLookup = apiProxy('schoolLookup', async (name: string) => {
  const { data } = await axios.get(
    `https://api.data.gov/ed/collegescorecard/v1/schools.json?fields=id,school.name&school.name=${encodeURIComponent(name)}&api_key=KWCevTbRUDsZtdQi5oSi5qbyENY8dWmdO68ihA5n`,
  );
  return data;
});

export type TransactionStatItem = {
  count?: number;
  credit: number;
  debit: number;
};

export type TransactionStatItemByMonth = TransactionStatItem & {
  year: number;
  month: number;
};
export type TransactionStats = {
  total: TransactionStatItem;
  ytd: TransactionStatItem;
  months: TransactionStatItemByMonth[];
};

export interface GetTransactionStatsParams {
  resourceId?: string;
  type?: string;
  start?: number;
  end?: number;
  sort?: string;
  sortDir?: string;
  filter?: string;
}
/** Fetch transaction statistics. */
export const getTransactionStats = apiProxy(
  'getTransactionStats',
  async ({
    resourceId,
    type,
    start,
    end,
    sort,
    sortDir,
    filter,
  }: GetTransactionStatsParams): Promise<TransactionStats> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [
      `start=${start || moment.utc().startOf('y').valueOf()}`,
      `end=${end || moment.utc().endOf('y').valueOf()}`,
    ];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    q.push(`resourceId=${resourceId}`);
    q.push(`type=${type}`);

    const { data } = await axios.get<TransactionStats>(
      `${process.env.ACCOUNT_API}/transactions/statistics?${q.join('&')}`,
    );
    return data;
  },
);

/** Fetch client transactions. */
export const getClientTransactions = apiProxy(
  'getClientTransactions',
  async (
    clientId: string,
    offset?: number,
    limit?: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
  ): Promise<{ total: number; transactions: Transaction[] }> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset || 0}`, `limit=${limit || 50}`];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    const { data } = await axios.get<{ total: number; transactions: Transaction[] }>(
      `${process.env.ACCOUNT_API}/transactions/clients/${clientId}?${q.join('&')}`,
    );
    return data;
  },
);

/** Fetch provider transactions. */
export const getProviderTransactions = apiProxy(
  'getProviderTransactions',
  async (
    providerId: string,
    offset?: number,
    limit?: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
  ): Promise<{ total: number; transactions: Transaction[] }> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset || 0}`, `limit=${limit || 50}`];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    const { data } = await axios.get<{ total: number; transactions: Transaction[] }>(
      `${process.env.ACCOUNT_API}/transactions/providers/${providerId}?${q.join('&')}`,
    );
    return data;
  },
);

/** Fetch user transactions. */
export const getUserTransactions = apiProxy(
  'getUserTransactions',
  async (
    userId: string,
    offset?: number,
    limit?: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
  ): Promise<{ total: number; transactions: Transaction[] }> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset || 0}`, `limit=${limit || 50}`];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    const { data } = await axios.get<{ total: number; transactions: Transaction[] }>(
      `${process.env.ACCOUNT_API}/transactions/users/${userId}?${q.join('&')}`,
    );
    return data;
  },
);

export const getPaymentMethodsByPhone = async (accountId: string, hostedPageId: string) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.get(`${process.env.ACCOUNT_API}/transactions/payment-methods/${accountId}`, {
    params: {
      hostedPageId,
    },
  });

  return data;
};

export const createSubscription = async (accountId: string, request: CreateSubscriptionRequest) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.post(
    `${process.env.ACCOUNT_API}/transactions/monthly-subscription/${accountId}`,
    request,
  );

  return data;
};

// Clean up orphaned licenses
export const cleanupLicenses = async (accountId: string) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.post(`${process.env.ACCOUNT_API}/accounts/${accountId}/cleanup-licenses`);

  return data;
};

// Replace addSubscriptionLicense with addOrActivateLicense
export const addOrActivateLicense = async (accountId: string, userId: string) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.post(
    `${process.env.ACCOUNT_API}/transactions/subscription/${accountId}/licenses`,
    null,
    {
      params: { userId },
    },
  );

  return data;
};

// Replace removeSubscriptionLicense with deactivateLicense
export const deactivateLicense = async (accountId: string, userId: string) => {
  try {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.put(
      `${process.env.ACCOUNT_API}/transactions/subscription/${accountId}/licenses/${userId}/deactivate`,
    );

    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      // Check for specific error message
      const errorDetail = error.response.data as { detail?: string };
      if (errorDetail.detail === "Cannot deactivate the primary provider's license") {
        throw new Error("Cannot deactivate the subscription owner's license");
      }
      if (errorDetail.detail?.includes('FILO')) {
        throw new Error('You can only deactivate the most recently activated license');
      }
    }
    throw error;
  }
};

export const createAnnualSubscription = async (accountId: string, request: CreateSubscriptionRequest) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.post(
    `${process.env.ACCOUNT_API}/transactions/annual-subscription/${accountId}`,
    request,
  );

  return data;
};

export const getCardTokenizationUrl = async (accountId: string): Promise<string> => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.get(`${process.env.ACCOUNT_API}/transactions/card-tokenization-url/${accountId}`);

  return data.url;
};

/** Update subscription amount */
export const updateSubscription = async (accountId: string, request: UpdateSubscriptionRequest) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.put(
    `${process.env.ACCOUNT_API}/transactions/monthly-subscription/${accountId}`,
    request,
  );

  return data;
};

/** Get subscription status */
export const getSubscriptionStatus = async (accountId: string) => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/subscription-status`);

  return data;
};

/** Request integration for an account */
export const requestIntegration = apiProxy(
  'requestIntegration',
  async (accountId: string, integrationType: string): Promise<IntegrationRequestDto> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post<IntegrationRequestDto>(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/request-integration`,
      JSON.stringify(integrationType),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return data;
  },
);

/** Get integration requests for an account */
export const getIntegrationRequests = apiProxy(
  'getIntegrationRequests',
  async (accountId: string): Promise<IntegrationRequestDto[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get<IntegrationRequestDto[]>(
      `${process.env.ACCOUNT_API}/accounts/${accountId}/integration-requests`,
    );
    return data;
  },
);

/** Test QuickBooks connectivity */
export interface QuickBooksConfigInfo {
  environment: string;
  hasClientId: boolean;
  hasClientSecret: boolean;
  hasRefreshToken: boolean;
  hasRealmId: boolean;
  redirectUri: string;
}

export interface QuickBooksTestResult {
  dnsResolved: boolean;
  dnsResolutionTimeMs: number;
  connectionSuccessful: boolean;
  connectionTimeMs: number;
  httpStatusCode?: number;
  responseContent?: string;
  errorMessage?: string;
  environment?: string;
  authenticationSuccessful: boolean;
  authenticationError?: string;
  authenticationTimeMs?: number;
  config?: QuickBooksConfigInfo;
}

export const testQuickBooksConnectivity = apiProxy(
  'testQuickBooksConnectivity',
  async (): Promise<QuickBooksTestResult> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/diagnostic/quickbooks/test`);
    return data;
  },
);

/** Get monthly signup counts. */
export const getMonthlySignupCounts = apiProxy(
  'getMonthlySignupCounts',
  async (
    accountId: string,
  ): Promise<{ year: number; month: number; clientCount: number; providerCount: number; expertCount: number }[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/monthly-signup-counts`);
    return data;
  },
);

/** Get total account counts. */
export const getAccountTotalCounts = apiProxy(
  'getAccountTotalCounts',
  async (): Promise<{
    total: number;
    providerTotal: number;
    clientTotal: number;
    expertTotal: number;
    inactiveProviderTotal: number;
  }> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/total-counts`);
    return data;
  },
);

/**
 * Get aggregated transaction statistics across all provider accounts.
 * Intended for the primary provider dashboard.
 */
export const getAllProvidersTransactionAggregates = async (
  accountId: string,
  year?: number,
  month?: number,
): Promise<AllProvidersTransactionAggregateDto> => {
  await getAccessToken(aadConfig().accountScopes); // Assuming account scopes are appropriate

  const params = new URLSearchParams();
  if (year !== undefined) {
    params.append('year', year.toString());
  }
  if (month !== undefined) {
    params.append('month', month.toString());
  }

  const queryString = params.toString();
  const url = `${process.env.ACCOUNT_API}/accounts/${accountId}/transactions/all-providers/aggregate${queryString ? `?${queryString}` : ''}`;

  const { data } = await axios.get<AllProvidersTransactionAggregateDto>(url);
  return data;
};

/**
 * A firm's own list of the direct costs it bills.
 *
 * Names only. What a line costs belongs to the phase it is on, because the same item costs
 * different money on different jobs - the catalog is so nobody retypes "Reprographics / Printing"
 * on every worksheet. The server seeds a firm's first read with the usual five.
 * @param accountId - The firm.
 * @param abort - Optional abort controller.
 * @returns The firm's catalog.
 */
export const getOdcItems = apiProxy(
  'getOdcItems',
  async (accountId: string, abort?: AbortController): Promise<OdcItem[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_HOST}/v1/odc-items/${accountId}`, {
      signal: abort?.signal,
    });
    return data;
  },
);

/**
 * Adds a cost to a firm's list, or brings back the one it already had.
 *
 * The server treats the same name as the same entry, so this is safe to call with something the
 * firm already has: it comes back rather than being duplicated, and a retired one is revived.
 * @param accountId - The firm.
 * @param name - What the firm calls this cost.
 * @returns The catalog entry, new or existing.
 */
export const postOdcItem = async (accountId: string, name: string): Promise<OdcItem> => {
  await getAccessToken(aadConfig().accountScopes);
  const { data } = await axios.post(`${process.env.ACCOUNT_HOST}/v1/odc-items/${accountId}`, { name });
  return data;
};

export const getAllProviderAccounts = apiProxy(
  'getAllProviderAccounts',
  async (accountId: string): Promise<Account[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${process.env.ACCOUNT_API}/accounts/${accountId}/provider-accounts/all`);
    return data;
  },
);

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// V2/FX methods!
//

export const getOfficesFx = apiProxy(
  'getOfficesFx',
  async (offset: number, limit: number, sort?: string, filter?: string): Promise<PagedItems<Office>> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset}`, `limit=${limit}`];
    if (sort) q.push(`sort=${encodeURIComponent(sort)}`);
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    return (await axios.get(`${process.env.ACCOUNT_HOST}/v1/offices${q.length ? `?${q.join('&')}` : ''}`)).data;
  },
);

export const getOfficesProjectCountsFx = apiProxy(
  'getOfficesProjectCountsFx',
  async (link: string): Promise<{ [key: string]: number }> => {
    await getAccessToken(aadConfig().accountScopes);
    return (await axios.get(link)).data;
  },
);

export const getLaborRates = apiProxy(
  'getLaborRates',
  async (accountId: string, sort?: string, filter?: string, abort?: AbortController): Promise<LaborRate[]> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [];
    if (sort) q.push(`sort=${encodeURIComponent(sort)}`);
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    const { data } = await axios.get(
      `${process.env.ACCOUNT_HOST}/v1/labor-rates/${accountId}${q.length ? `?${q.join('&')}` : ''}`,
      {
        signal: abort?.signal,
      },
    );
    return data;
  },
);

export const postLaborRate = apiProxy(
  'getLaborRates',
  async (
    accountId: string,
    customName: string,
    rate: number,
    /**
     * The kind of work, when the caller knows it. Omitted means Custom, which is what every row
     * created from settings is. The fee worksheet's Subject Matter Expert says so, because the
     * platform prices the kinds differently.
     */
    options?: { discipline?: number; showOnEstimates?: boolean },
    abort?: AbortController,
  ): Promise<LaborRate> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${process.env.ACCOUNT_HOST}/v1/labor-rates/${accountId}`,
      { customName, rate, ...options },
      { signal: abort?.signal },
    );
    return data;
  },
);
export const putLaborRate = apiProxy(
  'getLaborRates',
  async (
    accountId: string,
    id: string,
    patch: { rate?: number; customName?: string; isActive?: boolean; showOnEstimates?: boolean },
    abort?: AbortController,
  ): Promise<LaborRate[]> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.put(`${process.env.ACCOUNT_HOST}/v1/labor-rates/${accountId}/${id}`, patch, {
      signal: abort?.signal,
    });
    return data;
  },
);

export const deleteLaborRate = apiProxy(
  'getLaborRates',
  async (accountId: string, id: string, abort?: AbortController): Promise<void> => {
    await getAccessToken(aadConfig().accountScopes);
    await axios.delete(`${process.env.ACCOUNT_HOST}/v1/labor-rates/${accountId}/${id}`, {
      signal: abort?.signal,
    });
  },
);
